import { camera } from '$lib/core/render/camera';
import deployments from '$lib/deployments';
import { createDirectReadStore } from '$lib/onchain/direct-read';
import type { AvatarEntity, BombEntity } from '$lib/onchain/types';
import { zonesFetcher } from '$lib/onchain/zones-fetcher';
import { bigIntIDToXY } from 'bomber-world-contracts';
import { type LocalAction } from '../private/localState';

// Define the state type that the store will use
type OnchainState = {
	entities: { [id: string]: AvatarEntity | BombEntity };
	epoch: number;
};

const defaultState = (): OnchainState => ({
	entities: {},
	epoch: 0
});

export const onchainState = createDirectReadStore<OnchainState>(
	camera,
	defaultState,
	async (zones, fromBlock, toBlock, expectedEpoch) => {
		const zoneData = await zonesFetcher.fetchZones(zones, fromBlock, toBlock, expectedEpoch);

		if (!zoneData) {
			// the request has been dismissed
			return undefined;
		}

		const state: OnchainState = defaultState();

		const epoch = BigInt(zoneData.epoch);
		state.epoch = Number(epoch);

		// Separate old events (epoch - 2) from current events (epoch - 1)
		const events = zoneData.events.filter((v) => v.args.epoch == epoch - 1n);

		const avatarEvents: Map<
			bigint,
			import('viem').GetContractEventsReturnType<
				typeof deployments.contracts.Game.abi,
				'CommitmentRevealed',
				true
			>[0]
		> = new Map();
		for (const event of events) {
			avatarEvents.set(event.args.avatarID, event);
		}

		for (const entityFetched of zoneData.entities) {
			const id = entityFetched.avatarID.toString();

			let actions: LocalAction[] = [];

			const event = avatarEvents.get(entityFetched.avatarID);
			if (event) {
				actions = event.args.actions.map((v) => {
					const coords = bigIntIDToXY(v.data);
					return {
						type:
							v.actionType === 0
								? 'enter'
								: v.actionType === 1
									? 'move'
									: v.actionType === 2
										? 'exit'
										: v.actionType === 3
											? 'placeBomb'
											: 'placeBombDelayed',
						x: coords.x,
						y: coords.y
					};
				});
			}

			const { x, y } = bigIntIDToXY(entityFetched.position);
			const entity: AvatarEntity = {
				id,
				owner: entityFetched.owner,
				type: 'avatar',
				position: {
					x: Number(x),
					y: Number(y)
				},
				life: entityFetched.life,
				lastEpoch: Number(entityFetched.lastEpoch),
				previousActions: actions
			};
			state.entities[id] = entity;
		}

		for (const event of zoneData.events) {
			if (event.args.actions.length > 0) {
				let i = 0;
				for (const action of event.args.actions) {
					if (action.actionType === 3 || action.actionType === 4) {
						const startEpoch = Number(event.args.epoch);
						// PlaceBomb // PlaceBombDelayed
						const id = event.transactionHash + event.logIndex + i;
						// TODO the following can be something else as contract do not check for now
						//  we should either check in contract or use previous move actions values
						const { x, y } = bigIntIDToXY(action.data);
						const detonationEpoch = action.actionType === 3 ? startEpoch + 1 : startEpoch + 2; // 2 assume delay is fixed to 2

						if (detonationEpoch >= Number(epoch)) {
							const entity: BombEntity = {
								id,
								type: 'bomb',
								position: {
									x: Number(x),
									y: Number(y)
								},
								startEpoch,
								// TODO use action.data ? (need to use in contract roo)
								detonationEpoch
							};
							// console.debug(`bomb`, id, startEpoch, detonationEpoch);
							state.entities[id] = entity;
						}

						i++;
					}
				}
			}
		}

		return state;
	}
);
